import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "./AuthContext";
import AdminShell from "./AdminShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminTeam() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [editingEmail, setEditingEmail] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/designers`);
      setRows(data || []);
    } catch (e) {
      // Fallback: some older admins may not have designers endpoint
      try {
        const { data } = await axios.get(`${API}/auth/users`);
        setRows(data || []);
      } catch (err) {
        toast.error("Couldn't load team members");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) return;
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/auth/users`, {
        email: email.trim(),
        password,
        name: name.trim() || null,
      });
      toast.success(`Access granted to ${email.trim()}`);
      setEmail("");
      setName("");
      setPassword("");
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const remove = async (targetEmail) => {
    if (!window.confirm(`Remove ${targetEmail} from the team?`)) return;
    try {
      await axios.delete(`${API}/auth/users/${encodeURIComponent(targetEmail)}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <AdminShell eyebrow="Admin" title="Team access">
      <div style={{ color: "var(--folk-muted)", fontSize: 14, marginBottom: 20 }}>
        Anyone added here can sign in at <code>/login</code> and see every submission, PDF, and content control.
      </div>

        {/* Add member */}
        <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 24, marginBottom: 24 }}>
          <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 22, marginBottom: 4 }}>Invite a new teammate</div>
          <div style={{ color: "var(--folk-muted)", fontSize: 13.5, marginBottom: 20 }}>
            Send them the email + password below. They'll sign in at the same <code>/login</code> page you use.
          </div>
          <form onSubmit={create} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div>
              <p className="folk-tiny">Name (optional)</p>
              <input data-testid="new-name" className="folk-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya Dev" />
            </div>
            <div>
              <p className="folk-tiny">Email</p>
              <input data-testid="new-email" className="folk-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dev@example.com" />
            </div>
            <div style={{ gridColumn: "1 / span 2" }}>
              <p className="folk-tiny">Temporary password (min 8 characters)</p>
              <input data-testid="new-password" className="folk-input" type="text" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Give them something they'll change later" />
            </div>
            {error && (
              <div data-testid="team-error" style={{ gridColumn: "1 / span 2", color: "#B5603F", fontSize: 14 }}>{error}</div>
            )}
            <div style={{ gridColumn: "1 / span 2", display: "flex", justifyContent: "flex-end" }}>
              <button type="submit" disabled={creating} data-testid="add-btn" className="folk-pill" style={{ opacity: creating ? 0.6 : 1 }}>
                {creating ? "Adding…" : "Add teammate"}
              </button>
            </div>
          </form>
        </div>

        {/* List */}
        <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 8 }}>
          {loading ? (
            <div style={{ padding: 20, color: "var(--folk-muted)" }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 20, color: "var(--folk-muted)" }}>No teammates yet.</div>
          ) : (
            rows.map((r) => (
              <div
                key={r.email}
                data-testid={`team-row-${r.email}`}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px", borderTop: rows.indexOf(r) === 0 ? "none" : "1px solid var(--folk-border)", gap: 14, flexWrap: "wrap" }}
              >
                <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0, flex: 1 }}>
                  <TeamAvatar url={r.photo_url} name={r.name || r.email} size={44} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 17 }}>
                      {r.name || r.email}
                      {r.is_primary && (
                        <span style={{ marginLeft: 10, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--folk-accent)", border: "1px solid var(--folk-accent)", padding: "2px 8px", borderRadius: 999, fontFamily: "Host Grotesk, sans-serif" }}>
                          Primary
                        </span>
                      )}
                      {r.is_designer && (
                        <span style={{ marginLeft: 6, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0F3A54", background: "#BADDF0", padding: "2px 8px", borderRadius: 999, fontFamily: "Host Grotesk, sans-serif", fontWeight: 600 }}>
                          Designer
                        </span>
                      )}
                    </div>
                    <div style={{ color: "var(--folk-muted)", fontSize: 13, marginTop: 2 }}>
                      {r.email}
                      {r.created_at ? ` · added ${new Date(r.created_at).toLocaleDateString()}` : ""}
                    </div>
                    {r.tagline && (
                      <div style={{ color: "#5A3E2E", fontStyle: "italic", fontSize: 13, marginTop: 4, fontFamily: "Playfair Display, Georgia, serif" }}>
                        "{r.tagline}"
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                  <button
                    type="button"
                    data-testid={`edit-profile-${r.email}`}
                    onClick={() => setEditingEmail(r.email)}
                    style={{ border: "1.5px solid var(--folk-accent)", background: "transparent", color: "var(--folk-accent)", padding: "6px 14px", borderRadius: 999, cursor: "pointer", fontFamily: "Host Grotesk, sans-serif", fontSize: 13, fontWeight: 600 }}
                  >
                    Edit profile
                  </button>
                  {!r.is_primary && r.email !== user?.email && (
                    <button
                      type="button"
                      data-testid={`remove-${r.email}`}
                      onClick={() => remove(r.email)}
                      style={{ border: "1.5px solid var(--folk-border)", background: "transparent", color: "#B5603F", padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: "Host Grotesk, sans-serif", fontSize: 13 }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        {editingEmail && (
          <ProfileDrawer
            member={rows.find((r) => r.email === editingEmail)}
            onClose={() => setEditingEmail(null)}
            onSaved={(updated) => {
              setRows((prev) => prev.map((r) => r.email === updated.email ? { ...r, ...updated } : r));
            }}
          />
        )}
    </AdminShell>
  );
}

function TeamAvatar({ url, name, size = 44 }) {
  const initial = (name || "?").trim().slice(0, 1).toUpperCase();
  const style = { width: size, height: size, borderRadius: "50%", flexShrink: 0 };
  if (url) return <img src={url} alt={name || ""} style={{ ...style, objectFit: "cover", background: "#EFE5E1" }} />;
  return (
    <div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", background: "#FFFBC9", color: "#68280E", fontFamily: "Playfair Display, serif", fontSize: size * 0.42, fontWeight: 600 }}>
      {initial}
    </div>
  );
}

function ProfileDrawer({ member, onClose, onSaved }) {
  const [tagline, setTagline] = useState(member?.tagline || "");
  const [bio, setBio] = useState(member?.bio || "");
  const [years, setYears] = useState(member?.years_experience ?? "");
  const [specialties, setSpecialties] = useState((member?.specialties || []).join(", "));
  const [prefs, setPrefs] = useState(member?.contact_prefs || {});
  const [isDesigner, setIsDesigner] = useState(!!member?.is_designer);
  const [saving, setSaving] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(member?.photo_url || null);
  const [portfolio, setPortfolio] = useState(member?.portfolio || []);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const photoInputRef = useRef(null);
  const portfolioInputRef = useRef(null);

  if (!member) return null;

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        tagline: tagline.trim() || null,
        bio: bio.trim() || null,
        years_experience: years === "" ? null : Number(years),
        specialties: specialties.split(",").map((s) => s.trim()).filter(Boolean),
        contact_prefs: {
          whatsapp: (prefs.whatsapp || "").trim() || null,
          email_public: (prefs.email_public || "").trim() || null,
          call: (prefs.call || "").trim() || null,
        },
        is_designer: isDesigner,
      };
      const { data } = await axios.put(`${API}/auth/users/${encodeURIComponent(member.email)}/profile`, body);
      toast.success("Profile saved");
      onSaved && onSaved({ ...data, photo_url: photoUrl, portfolio });
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/auth/users/${encodeURIComponent(member.email)}/photo`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(data.photo_url);
      onSaved && onSaved({ email: member.email, photo_url: data.photo_url });
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Photo upload failed");
    } finally { setUploadingPhoto(false); if (photoInputRef.current) photoInputRef.current.value = ""; }
  };

  const addPortfolio = async (files) => {
    if (!files?.length) return;
    setUploadingPortfolio(true);
    for (const f of Array.from(files)) {
      if (portfolio.length >= 3) { toast.warning("Max 3 portfolio images"); break; }
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await axios.post(`${API}/auth/users/${encodeURIComponent(member.email)}/portfolio`, form, { headers: { "Content-Type": "multipart/form-data" } });
        setPortfolio((prev) => [...prev, data.entry]);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      }
    }
    setUploadingPortfolio(false);
    if (portfolioInputRef.current) portfolioInputRef.current.value = "";
  };

  const removePortfolio = async (entry) => {
    try {
      await axios.delete(`${API}/auth/users/${encodeURIComponent(member.email)}/portfolio/${entry.id}`);
      setPortfolio((prev) => prev.filter((p) => p.id !== entry.id));
    } catch (err) {
      toast.error("Remove failed");
    }
  };

  return (
    <div
      data-testid="profile-drawer"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(15, 12, 10, 0.4)", display: "flex", justifyContent: "flex-end" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(520px, 100%)", height: "100%", background: "#FFFBF3", overflowY: "auto", padding: 28, boxShadow: "-20px 0 40px rgba(0,0,0,0.16)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 11.5, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700 }}>Designer profile</div>
          <button data-testid="profile-drawer-close" type="button" onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 22, cursor: "pointer", color: "var(--folk-muted)" }}>×</button>
        </div>
        <h2 className="folk-serif" style={{ fontSize: 26, lineHeight: 1.2, margin: "6px 0 20px", textTransform: "lowercase", letterSpacing: "-0.028em" }}>
          editing {(member.name || member.email).toLowerCase()}
        </h2>

        <div style={{ display: "flex", gap: 18, alignItems: "center", marginBottom: 22 }}>
          <TeamAvatar url={photoUrl} name={member.name || member.email} size={80} />
          <div>
            <input ref={photoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="profile-photo-input" />
            <button type="button" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} data-testid="profile-photo-btn" className="folk-pill" style={{ fontSize: 13, opacity: uploadingPhoto ? 0.6 : 1 }}>
              {uploadingPhoto ? "Uploading…" : (photoUrl ? "Replace photo" : "Add photo")}
            </button>
          </div>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, fontFamily: "Host Grotesk, sans-serif", fontSize: 14 }}>
          <input type="checkbox" checked={isDesigner} onChange={(e) => setIsDesigner(e.target.checked)} data-testid="profile-is-designer" style={{ width: 18, height: 18 }} />
          Show this person as a designer on the homeowner portal
        </label>

        <ProfileField label="Tagline" testId="profile-tagline">
          <input className="folk-input" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="A one-line description of your work" style={{ padding: "10px 14px", fontSize: 14, width: "100%" }} data-testid="profile-tagline-input" />
        </ProfileField>

        <ProfileField label="Short bio" testId="profile-bio">
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} placeholder="How you work, what makes you tick." style={{ padding: "12px 14px", fontSize: 14, borderRadius: 14, border: "1.5px solid var(--folk-border)", background: "#FFFFFF", width: "100%", resize: "vertical", fontFamily: "inherit" }} data-testid="profile-bio-input" />
        </ProfileField>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <ProfileField label="Years designing" testId="profile-years">
            <input className="folk-input" type="number" min="0" value={years} onChange={(e) => setYears(e.target.value)} style={{ padding: "10px 14px", fontSize: 14, width: "100%" }} data-testid="profile-years-input" />
          </ProfileField>
          <ProfileField label="Specialties (comma-separated)" testId="profile-specialties">
            <input className="folk-input" value={specialties} onChange={(e) => setSpecialties(e.target.value)} placeholder="Warm minimalism, Slow craft" style={{ padding: "10px 14px", fontSize: 14, width: "100%" }} data-testid="profile-specialties-input" />
          </ProfileField>
        </div>

        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700, marginBottom: 10 }}>Contact prefs (shown on portal)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
            {[
              { key: "whatsapp", label: "WhatsApp number", placeholder: "+91 …" },
              { key: "email_public", label: "Public email", placeholder: "you@folkobjects.com" },
              { key: "call", label: "Phone (for call CTA)", placeholder: "+91 …" },
            ].map((p) => (
              <input
                key={p.key}
                className="folk-input"
                value={prefs[p.key] || ""}
                onChange={(e) => setPrefs((cur) => ({ ...cur, [p.key]: e.target.value }))}
                placeholder={`${p.label} — ${p.placeholder}`}
                style={{ padding: "10px 14px", fontSize: 14 }}
                data-testid={`profile-pref-${p.key}`}
              />
            ))}
          </div>
        </div>

        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700, marginBottom: 10 }}>Portfolio (up to 3)</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 10 }}>
            {portfolio.map((p) => (
              <div key={p.id} data-testid={`portfolio-entry-${p.id}`} style={{ position: "relative", borderRadius: 10, overflow: "hidden", background: "#EFE5E1" }}>
                <img src={p.url} alt={p.caption || ""} style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }} />
                <button type="button" onClick={() => removePortfolio(p)} data-testid={`portfolio-remove-${p.id}`} style={{ position: "absolute", top: 6, right: 6, background: "rgba(15,12,10,0.7)", border: "none", color: "#FFF", borderRadius: 999, width: 24, height: 24, fontSize: 14, cursor: "pointer", lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
          {portfolio.length < 3 && (
            <>
              <input ref={portfolioInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => addPortfolio(e.target.files)} data-testid="portfolio-input" />
              <button type="button" onClick={() => portfolioInputRef.current?.click()} disabled={uploadingPortfolio} data-testid="portfolio-btn" style={{ padding: "10px 16px", borderRadius: 12, border: "1.5px dashed rgba(15,58,84,0.4)", background: "#F3F8FB", color: "#0F3A54", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                {uploadingPortfolio ? "Uploading…" : "+ Add portfolio images"}
              </button>
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 28, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "1.5px solid var(--folk-border)", padding: "10px 18px", borderRadius: 999, cursor: "pointer", fontFamily: "Host Grotesk, sans-serif", fontSize: 13, color: "var(--folk-muted)" }}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} data-testid="profile-save" className="folk-pill" style={{ opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileField({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12, fontFamily: "Host Grotesk, sans-serif" }}>
      <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700 }}>{label}</span>
      {children}
    </label>
  );
}
